<template>
  <div class="mobile-header">
    <div class="header-left">
      <button
        type="button"
        class="back-nav"
        :aria-label="$t('common.back')"
        @click="$emit('go-back')"
      >
        <span class="back-button" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2.5"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </span>
        <div class="mobile-flag-container">
          <CountryAvatar
            v-if="isLite"
            :country="lottery?.country"
            :fallback-label="lottery?.game_name"
            size="sm"
          />
          <img 
            v-else-if="lottery?.game_logo" 
            :src="getGameLogoUrl(lottery.game_logo, lottery.game_slug)" 
            :alt="lottery.country + ' flag'" 
            class="mobile-flag" 
          >
        </div>
      </button>

      <div class="mobile-lottery-details">
        <div
          class="mobile-lottery-title-row"
          :class="{ 'has-watch-live': showWatchLive }"
        >
          <h1 class="mobile-lottery-name">
            {{ displayGameName }}
          </h1>
          <button
            v-if="showWatchLive"
            type="button"
            class="watch-live-btn"
            @click="$emit('watch-live')"
          >
            <span class="watch-live-icon" aria-hidden="true">▶</span>
            {{ $t('detail.watchLive') }}
          </button>
        </div>
      </div>
    </div>
    
    <div class="mobile-countdown-section">
      <div class="mobile-countdown">
        <div class="mobile-time-block">
          <span class="mobile-time">{{ countdown.hours }}</span>
          <span class="mobile-label">{{ $t('time.hours') }}</span>
        </div>
        <div class="mobile-time-block">
          <span class="mobile-time">{{ countdown.minutes }}</span>
          <span class="mobile-label">{{ $t('time.mins') }}</span>
        </div>
        <div class="mobile-time-block">
          <span class="mobile-time">{{ countdown.seconds }}</span>
          <span class="mobile-label">{{ $t('time.secs') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, withDefaults } from 'vue';
import { getGameLogoUrl } from '../../utils/resultParser';
import { useIntegrationMode } from '@/composables/useIntegrationMode';
import CountryAvatar from '@/components/base/CountryAvatar.vue';

const { isLite } = useIntegrationMode();

interface Countdown {
  hours: string;
  minutes: string;
  seconds: string;
}

interface Props {
  gameName?: string;
  gameFormat?: string;
  countryFlag?: string;
  countryName?: string;
  countdown: Countdown;
  lottery?: any;
  showWatchLive?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  gameName: '',
  gameFormat: '',
  countryFlag: '',
  countryName: '',
  lottery: null,
});

defineEmits(['go-back', 'watch-live']);

const displayGameName = computed(() => props.lottery?.name || props.gameName || '');
</script>

<style scoped>
.mobile-header {
  height: 64px;
  min-height: 64px;
  padding: 0;
  background: var(--surface-detail-game-header);
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  /* Divider so content does not visually merge while scrolling */
  border-bottom: 1px solid var(--detail-mobile-header-divider, var(--colour-surface-cont, rgba(255, 255, 255, 0.18)));
}

.header-left {
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: 2px;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
  overflow: visible;
}

.back-nav {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  align-self: center;
  gap: 2px;
  height: 32px;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  overflow: visible;
  -webkit-tap-highlight-color: transparent;
}

.back-button {
  background: none;
  border: none;
  color: var(--icon-detail-game-header);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 22px;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
  overflow: visible;
  pointer-events: none;
  line-height: 0;
}

.back-button svg {
  width: 20px;
  height: 20px;
  display: block;
  /* Chevron glyphs sit optically high in the viewBox */
  transform: translateY(2px);
}

.mobile-flag-container {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--detail-header-flag-bg, var(--surface-detail-game-header));
  border: 1px solid var(--detail-header-flag-border, var(--border-detail-card));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  pointer-events: none;
  margin: 0;
}

.mobile-flag {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.mobile-lottery-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
}

.mobile-lottery-title-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.15rem;
  min-width: 0;
  width: 100%;
}

.mobile-lottery-name {
  font-size: 14px;
  font-weight: 400;
  color: var(--text-detail-game-header);
  margin: 0;
  min-width: 0;
  max-width: 100%;
  line-height: 1.15;
  /* Wrap up to 2 lines when Watch Live is absent */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
}

.mobile-lottery-title-row.has-watch-live .mobile-lottery-name {
  white-space: nowrap;
  display: block;
  -webkit-line-clamp: unset;
  text-overflow: ellipsis;
}

.watch-live-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: color-mix(in srgb, #22c55e 10%, transparent);
  color: #15803d;
  border: 1px solid #16a34a;
  border-radius: 4px;
  padding: 0.1rem 0.35rem;
  font-size: 0.62rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.watch-live-icon {
  font-size: 0.62rem;
  color: #16a34a;
  line-height: 1;
}

/* Dark mode: white label + green outline (meeting review) */
:global(html.theme-dark) .watch-live-btn,
:global(body.theme-dark) .watch-live-btn {
  background: transparent;
  color: #ffffff;
  border-color: #22c55e;
}

:global(html.theme-dark) .watch-live-icon,
:global(body.theme-dark) .watch-live-icon {
  color: #22c55e;
}

.mobile-countdown-section {
  padding: 0 6px;
  background: var(--surface-detail-countdown);
  align-self: stretch;
  height: auto;
  min-height: 100%;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 40%;
  box-sizing: border-box;
}

.mobile-countdown {
  display: flex;
  gap: 6px;
}

.mobile-time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 22px;
}

.mobile-time {
  color: var(--text-detail-countdown, var(--text-detail-game-header, var(--colour-text-inverse)));
  text-align: center;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--detail-countdown-mobile-time-size, 14px);
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.mobile-label {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--detail-countdown-mobile-label-size, 9px);
  font-weight: 400;
  color: var(--text-detail-countdown-muted);
  text-transform: none;
  margin-top: 0;
  letter-spacing: 0.2px;
  line-height: 1;
}

@media (max-width: 480px) {
  .mobile-header {
    height: 58px;
    min-height: 58px;
    padding: 0;
  }
  
  .header-left {
    padding-left: 2px;
    gap: 6px;
  }

  .back-nav {
    gap: 2px;
    height: 30px;
  }

  .back-button {
    width: 20px;
    height: 30px;
  }

  .back-button svg {
    width: 18px;
    height: 18px;
    transform: translateY(2px);
  }

  .mobile-lottery-name {
    font-size: 13px;
  }

  .mobile-flag-container {
    width: 28px;
    height: 28px;
  }

  .mobile-countdown-section {
    padding: 0 4px;
    max-width: 40%;
  }

  .mobile-time {
    font-size: var(--detail-countdown-mobile-time-size, 13px);
  }

  .mobile-label {
    font-size: var(--detail-countdown-mobile-label-size, 8px);
  }
  
  .mobile-countdown {
    gap: 4px;
  }
  
  .mobile-time-block {
    min-width: 20px;
  }

  .mobile-lottery-title-row {
    gap: 0.1rem;
  }
}
</style>
